import React, { useState, useEffect, useRef } from "react";
import { shortnString } from "@_utils/helpers";
import IconButton from "@mui/material/IconButton";
import styles from "./SearchByType.module.css";
import TextfieldInput from "@_components/TextfieldInput/TextfieldInput";
import Clear from "@mui/icons-material/Clear";

/**
 * Reusable "search by type" component.
 *
 * This component is intentionally "dumb":
 * - Parent owns the search value and selected tag state
 * - Parent passes callbacks for change / select / clear
 */
function SearchByType({
  label,
  searchValue,
  selectedSearchTag,
  casesSearchTag = [],
  autocompleteOptions,
  onSearchInputChange,
  onSearchTagSelect,
  onAutocompleteOptionSelect,
  onClearClick,
  noOptionsText,
}) {
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchInputRef = useRef(null);

  const handleSearchInputFocus = () => {
    setShowSearchDropdown(true);
  };

  const handleLocalClearClick = () => {
    if (onClearClick) {
      onClearClick();
    }
    setShowSearchDropdown(false);
  };

  const handleLocalTagSelect = (option) => {
    if (onSearchTagSelect) {
      onSearchTagSelect(option);
      searchInputRef.current.focus();
    }
    setShowSearchDropdown(true); // keep it open so autocomplete can appear
  };

  const handleAutocompleteSelect = (option) => {
    if (onAutocompleteOptionSelect) {
      onAutocompleteOptionSelect(option);
    }
    setShowSearchDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showSearchDropdown &&
        !event.target.closest(`.${styles.search_container}`)
      ) {
        setShowSearchDropdown(false);
      }
    };

    if (showSearchDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchDropdown]);

  return (
    <div className={styles.search_input_container}>
      <div className={styles.search_container}>
        <TextfieldInput
          label={label}
          id={"case_search"}
          name={"case_search"}
          value={searchValue}
          onChange={(e) => {
            if (onSearchInputChange) {
              onSearchInputChange(e);
            }
            // When user starts typing, ensure dropdown is visible
            setShowSearchDropdown(true);
          }}
          onFocus={handleSearchInputFocus}
          className={styles.input}
          autoComplete="off"
          inputRef={searchInputRef}
          InputProps={{
            ...(selectedSearchTag && {
              startAdornment: (
                <div className={styles.search_tag}>
                  <span>{selectedSearchTag.label}</span>
                </div>
              ),
            }),
            ...((searchValue || selectedSearchTag) && {
              endAdornment: (
                <IconButton
                  sx={{
                    visibility:
                      searchValue || selectedSearchTag ? "visible" : "hidden",
                  }}
                  onClick={handleLocalClearClick}
                  style={{ width: 25, height: 25 }}
                  size="large">
                  <Clear />
                </IconButton>
              ),
            }),
          }}
        />

        {/* Step 1: choose search type (case id / domain / etc.) */}
        {showSearchDropdown && !selectedSearchTag && (
          <div className={styles.search_dropdown}>
            {casesSearchTag.map((option) => (
              <div
                key={option.value}
                className={styles.dropdown_option}
                onClick={() => handleLocalTagSelect(option)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}

        {/* Step 2: after tag is selected, show autocomplete options or no results message */}
        {showSearchDropdown &&
          selectedSearchTag &&
          autocompleteOptions &&
          (autocompleteOptions.length > 0 || searchValue) && (
            <div className={styles.search_dropdown_autocomplete}>
              {autocompleteOptions.length > 0 ? (
                <>
                  {/* hardcoded header, if this is needed somewhere else we can add prop and make it dynamic */}
                  <div className={styles.dropdown_header}>
                    <span>ID</span>
                    <span>Domain</span>
                  </div>

                  {/* dynamic rows */}
                  {autocompleteOptions.map((option) => (
                    <div
                      key={option.value}
                      className={styles.dropdown_option_autocomplete}
                      onClick={() => handleAutocompleteSelect(option)}
                    >
                      <span>{option.caseId}</span>
                      <span
                        className={styles.domain}
                        style={{ whiteSpace: "nowrap", overflow: "hidden" }}
                      >
                        {shortnString(option.domain)}
                      </span>
                    </div>
                  ))}
                </>
              ) : (
                // Show "No case found" when there are no options and user has typed something
                (<div className={styles.no_options_message}>{noOptionsText}</div>)
              )}
            </div>
          )}
      </div>
    </div>
  );
}

export default SearchByType;
